import React, { useState, useEffect, useRef } from "react";
import { useAuth } from "../context/AuthContext";
import { useNavigate, Link } from "react-router-dom";
import { companiesAPI } from "../lib/api";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Label } from "../components/ui/label";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Separator } from "../components/ui/separator";
import { Switch } from "../components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { toast } from "sonner";
import {
  Building2,
  Upload,
  Trash2,
  Palette,
  Image as ImageIcon,
  Save,
  Loader2,
  Eye,
  Mail,
  ChevronRight,
  FileImage,
  Settings2
} from "lucide-react";

export default function CompanySettingsPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [companies, setCompanies] = useState([]);
  const [selectedCompanyId, setSelectedCompanyId] = useState(user?.active_company_id || "");
  const [company, setCompany] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [uploadingCover, setUploadingCover] = useState(false);
  const [savingColors, setSavingColors] = useState(false);
  const [primaryColor, setPrimaryColor] = useState("#f59e0b");
  const [secondaryColor, setSecondaryColor] = useState("#1e3a5f");
  const fileInputRef = useRef(null);
  const coverInputRef = useRef(null);
  
  // VAT settings state
  const [vatSettings, setVatSettings] = useState({
    vat_enabled_wallbox: true,
    vat_enabled_pv: false,
    vat_enabled_storage: false,
    vat_enabled_other: false,
    default_vat_rate: 19.0,
    default_labor_cost_percentage: 30.0,
    vat_label_wallbox: "Lieferung und Montage von Wallboxen",
    vat_label_pv: "Gemäß §12 Abs. 3 UStG sind PV-Anlagen unter 30kWp MwSt-frei",
    vat_label_storage: "Batteriespeicher und Zubehör",
    vat_label_other: "Alle anderen Produkte und Dienstleistungen"
  });
  const [savingVat, setSavingVat] = useState(false);

  // AB (Auftragsbestätigung) text blocks
  const [abTexts, setAbTexts] = useState({
    order_confirmation_greeting: "",
    order_confirmation_body: "",
    order_confirmation_footer: "",
    order_confirmation_show_payment_terms: true,
    order_confirmation_payment_terms: ""
  });
  const [savingAbTexts, setSavingAbTexts] = useState(false);

  useEffect(() => {
    loadCompanies();
  }, []);

  useEffect(() => {
    if (selectedCompanyId) {
      loadCompany(selectedCompanyId);
    }
  }, [selectedCompanyId]);

  const loadCompanies = async () => {
    try {
      // Superadmin can see all companies, others see their assigned companies
      if (user?.role === "superadmin") {
        const response = await companiesAPI.list();
        setCompanies(response.data);
        // Auto-select first company if none selected
        if (!selectedCompanyId && response.data.length > 0) {
          setSelectedCompanyId(response.data[0].id);
        }
      } else if (user?.company_ids?.length > 0) {
        // For admin/vertriebler, use their active company
        setSelectedCompanyId(user.active_company_id);
      }
    } catch (error) {
      console.error("Failed to load companies:", error);
    } finally {
      setLoading(false);
    }
  };

  const loadCompany = async (companyId) => {
    if (!companyId) return;

    try {
      const response = await companiesAPI.get(companyId);
      setCompany(response.data);
      setPrimaryColor(response.data.primary_color || "#f59e0b");
      setSecondaryColor(response.data.secondary_color || "#1e3a5f");
      
      // Load VAT settings
      try {
        const vatResponse = await companiesAPI.getVatSettings(companyId);
        setVatSettings(vatResponse.data);
      } catch (vatErr) {
        console.error("Failed to load VAT settings:", vatErr);
      }

      // Load AB texts from company data
      setAbTexts({
        order_confirmation_greeting: response.data.order_confirmation_greeting || "",
        order_confirmation_body: response.data.order_confirmation_body || "",
        order_confirmation_footer: response.data.order_confirmation_footer || "",
        order_confirmation_show_payment_terms: response.data.order_confirmation_show_payment_terms !== false,
        order_confirmation_payment_terms: response.data.order_confirmation_payment_terms || ""
      });
    } catch (error) {
      console.error("Failed to load company:", error);
      toast.error("Fehler beim Laden der Firmendaten");
    }
  };

  const handleSaveAbTexts = async () => {
    setSavingAbTexts(true);
    try {
      // Dedizierter Endpoint – nur die AB-Felder, nichts überschreiben.
      await companiesAPI.updateAbTextBlocks(selectedCompanyId, abTexts);
      toast.success("AB-Textbausteine gespeichert");
      await loadCompany(selectedCompanyId);
    } catch (error) {
      console.error("Failed to save AB texts:", error);
      toast.error(error.response?.data?.detail || "Fehler beim Speichern der AB-Textbausteine");
    } finally {
      setSavingAbTexts(false);
    }
  };

  const handleSaveVatSettings = async () => {
    setSavingVat(true);
    try {
      await companiesAPI.updateVatSettings(selectedCompanyId, vatSettings);
      toast.success("MwSt-Einstellungen gespeichert. Änderungen wirken sich nur auf neue Dokumente aus.");
    } catch (error) {
      console.error("Failed to save VAT settings:", error);
      toast.error("Fehler beim Speichern der MwSt-Einstellungen");
    } finally {
      setSavingVat(false);
    }
  };

  const handleLogoUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ["image/png", "image/jpeg", "image/jpg", "image/webp"];
    if (!allowedTypes.includes(file.type)) {
      toast.error("Nur PNG, JPEG oder WebP Dateien erlaubt");
      return;
    }

    // Validate file size (max 2MB)
    if (file.size > 2 * 1024 * 1024) {
      toast.error("Logo darf maximal 2MB groß sein");
      return;
    }

    setUploading(true);
    try {
      await companiesAPI.uploadLogo(selectedCompanyId, file);
      toast.success("Logo erfolgreich hochgeladen");
      await loadCompany(selectedCompanyId);
    } catch (error) {
      console.error("Failed to upload logo:", error);
      toast.error(error.response?.data?.detail || "Fehler beim Hochladen des Logos");
    } finally {
      setUploading(false);
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  };

  const handleDeleteLogo = async () => {
    if (!confirm("Möchten Sie das Logo wirklich löschen?")) return;

    try {
      await companiesAPI.deleteLogo(selectedCompanyId);
      toast.success("Logo erfolgreich entfernt");
      await loadCompany(selectedCompanyId);
    } catch (error) {
      console.error("Failed to delete logo:", error);
      toast.error("Fehler beim Löschen des Logos");
    }
  };

  const handleCoverUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ["image/png", "image/jpeg", "image/jpg", "image/webp"];
    if (!allowedTypes.includes(file.type)) {
      toast.error("Nur PNG, JPEG oder WebP Dateien erlaubt");
      return;
    }

    // Validate file size (max 5MB for cover images)
    if (file.size > 5 * 1024 * 1024) {
      toast.error("Deckblatt-Bild darf maximal 5MB groß sein");
      return;
    }

    setUploadingCover(true);
    try {
      await companiesAPI.uploadCoverImage(selectedCompanyId, file);
      toast.success("Deckblatt-Bild erfolgreich hochgeladen");
      await loadCompany(selectedCompanyId);
    } catch (error) {
      console.error("Failed to upload cover image:", error);
      toast.error(error.response?.data?.detail || "Fehler beim Hochladen des Deckblatt-Bildes");
    } finally {
      setUploadingCover(false);
      if (coverInputRef.current) {
        coverInputRef.current.value = "";
      }
    }
  };

  const handleDeleteCover = async () => {
    if (!confirm("Möchten Sie das Deckblatt-Bild wirklich löschen?")) return;

    try {
      await companiesAPI.deleteCoverImage(selectedCompanyId);
      toast.success("Deckblatt-Bild erfolgreich entfernt");
      await loadCompany(selectedCompanyId);
    } catch (error) {
      console.error("Failed to delete cover image:", error);
      toast.error("Fehler beim Löschen des Deckblatt-Bildes");
    }
  };

  const handleSaveColors = async () => {
    setSavingColors(true);
    try {
      await companiesAPI.updateCI(selectedCompanyId, {
        primary_color: primaryColor,
        secondary_color: secondaryColor
      });
      toast.success("CI-Farben erfolgreich gespeichert");
      await loadCompany(selectedCompanyId);
    } catch (error) {
      console.error("Failed to save colors:", error);
      toast.error("Fehler beim Speichern der Farben");
    } finally {
      setSavingColors(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!selectedCompanyId && companies.length === 0) {
    return (
      <div className="space-y-6 animate-slide-in max-w-2xl" data-testid="company-settings-page">
        <div>
          <h1 className="text-3xl font-bold font-['Manrope']">Firmen CI</h1>
          <p className="text-muted-foreground">
            Keine Firmen verfügbar
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-slide-in max-w-3xl" data-testid="company-settings-page">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold font-['Manrope']">Firmen CI</h1>
        <p className="text-muted-foreground">
          Corporate Identity Einstellungen für PDF-Angebote
        </p>
      </div>

      {/* Company Selector for Superadmin */}
      {user?.role === "superadmin" && companies.length > 1 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Building2 className="h-5 w-5" />
              Firma auswählen
            </CardTitle>
            <CardDescription>Wählen Sie die Firma, deren CI Sie bearbeiten möchten</CardDescription>
          </CardHeader>
          <CardContent>
            <Select value={selectedCompanyId} onValueChange={setSelectedCompanyId}>
              <SelectTrigger data-testid="company-select">
                <SelectValue placeholder="Firma auswählen" />
              </SelectTrigger>
              <SelectContent>
                {companies.map((c) => (
                  <SelectItem key={c.id} value={c.id}>
                    {c.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </CardContent>
        </Card>
      )}

      {company && (
        <>
          {/* Company Name Display */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Building2 className="h-5 w-5" />
                Firma
              </CardTitle>
              <CardDescription>Firmenname</CardDescription>
            </CardHeader>
            <CardContent>
              <p className="text-xl font-semibold">{company?.name}</p>
            </CardContent>
          </Card>

          {/* Company Address & Contact - Editable */}
          <CompanyInfoCard company={company} onUpdate={() => loadCompany(selectedCompanyId)} />

          {/* Tax Information - Important for invoicing */}
          <TaxInfoCard company={company} onUpdate={() => loadCompany(selectedCompanyId)} />
          
          {/* Bank Information - Important for invoicing */}
          <BankInfoCard company={company} onUpdate={() => loadCompany(selectedCompanyId)} />

      {/* Logo Upload */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <ImageIcon className="h-5 w-5" />
            Firmenlogo
          </CardTitle>
          <CardDescription>
            Logo für Angebots-PDFs (empfohlen: 500x200px, max. 2MB)
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Current Logo Preview */}
          {company?.logo_url ? (
            <div className="space-y-4">
              <div className="border rounded-lg p-4 bg-white dark:bg-gray-900">
                <p className="text-sm text-muted-foreground mb-2">Aktuelles Logo:</p>
                <img
                  src={company.logo_url}
                  alt="Firmenlogo"
                  className="max-h-24 object-contain"
                  data-testid="company-logo-preview"
                />
              </div>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploading}
                  data-testid="change-logo-btn"
                >
                  {uploading ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <Upload className="h-4 w-4 mr-2" />
                  )}
                  Logo ändern
                </Button>
                <Button
                  variant="outline"
                  onClick={handleDeleteLogo}
                  className="text-destructive hover:text-destructive"
                  data-testid="delete-logo-btn"
                >
                  <Trash2 className="h-4 w-4 mr-2" />
                  Löschen
                </Button>
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="border-2 border-dashed rounded-lg p-8 text-center">
                <ImageIcon className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                <p className="text-muted-foreground mb-4">
                  Kein Logo hochgeladen
                </p>
                <Button
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploading}
                  data-testid="upload-logo-btn"
                >
                  {uploading ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <Upload className="h-4 w-4 mr-2" />
                  )}
                  Logo hochladen
                </Button>
              </div>
            </div>
          )}

          <input
            ref={fileInputRef}
            type="file"
            accept="image/png,image/jpeg,image/jpg,image/webp"
            onChange={handleLogoUpload}
            className="hidden"
            data-testid="logo-file-input"
          />
        </CardContent>
      </Card>

      {/* Cover Page Editor Link */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileImage className="h-5 w-5" />
            Angebots-Deckblatt
          </CardTitle>
          <CardDescription>
            Gestalten Sie das Deckblatt für Ihre Angebots-PDFs
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-between p-4 border rounded-lg hover:bg-muted/50 transition-colors">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-primary/10 rounded-lg">
                <Settings2 className="h-6 w-6 text-primary" />
              </div>
              <div>
                <p className="font-medium">Deckblatt-Editor</p>
                <p className="text-sm text-muted-foreground">
                  Hintergrundbild, Logo, Überschrift und Info-Box konfigurieren
                </p>
              </div>
            </div>
            <Link to="/firmen-ci/deckblatt">
              <Button data-testid="open-cover-editor-btn">
                <Settings2 className="h-4 w-4 mr-2" />
                Editor öffnen
                <ChevronRight className="h-4 w-4 ml-2" />
              </Button>
            </Link>
          </div>
        </CardContent>
      </Card>

      {/* CI Colors */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Palette className="h-5 w-5" />
            CI-Farben
          </CardTitle>
          <CardDescription>
            Farben für Angebots-PDFs und Dokumente
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Primary Color */}
            <div className="space-y-2">
              <Label htmlFor="primary-color">Primärfarbe (Akzent)</Label>
              <div className="flex gap-2">
                <div
                  className="w-12 h-10 rounded-md border cursor-pointer"
                  style={{ backgroundColor: primaryColor }}
                  onClick={() => document.getElementById("primary-color-input")?.click()}
                />
                <Input
                  id="primary-color"
                  type="text"
                  value={primaryColor}
                  onChange={(e) => setPrimaryColor(e.target.value)}
                  placeholder="#f59e0b"
                  className="font-mono"
                  data-testid="primary-color-input"
                />
                <input
                  id="primary-color-input"
                  type="color"
                  value={primaryColor}
                  onChange={(e) => setPrimaryColor(e.target.value)}
                  className="sr-only"
                />
              </div>
              <p className="text-xs text-muted-foreground">
                Wird für Überschriften und Preise verwendet
              </p>
            </div>

            {/* Secondary Color */}
            <div className="space-y-2">
              <Label htmlFor="secondary-color">Sekundärfarbe (Tabellen)</Label>
              <div className="flex gap-2">
                <div
                  className="w-12 h-10 rounded-md border cursor-pointer"
                  style={{ backgroundColor: secondaryColor }}
                  onClick={() => document.getElementById("secondary-color-input")?.click()}
                />
                <Input
                  id="secondary-color"
                  type="text"
                  value={secondaryColor}
                  onChange={(e) => setSecondaryColor(e.target.value)}
                  placeholder="#1e3a5f"
                  className="font-mono"
                  data-testid="secondary-color-input"
                />
                <input
                  id="secondary-color-input"
                  type="color"
                  value={secondaryColor}
                  onChange={(e) => setSecondaryColor(e.target.value)}
                  className="sr-only"
                />
              </div>
              <p className="text-xs text-muted-foreground">
                Wird für Tabellenköpfe und Linien verwendet
              </p>
            </div>
          </div>

          {/* Preview */}
          <div className="space-y-2">
            <Label>Vorschau</Label>
            <div className="border rounded-lg p-4 bg-white dark:bg-gray-900">
              <div className="space-y-3">
                <div className="flex items-center gap-3">
                  {company?.logo_url && (
                    <img
                      src={company.logo_url}
                      alt="Logo"
                      className="h-8 object-contain"
                    />
                  )}
                  <span
                    className="text-lg font-bold"
                    style={{ color: primaryColor }}
                  >
                    {company?.name}
                  </span>
                </div>
                <div
                  className="h-8 rounded flex items-center px-3 text-white text-sm font-medium"
                  style={{ backgroundColor: secondaryColor }}
                >
                  Tabellenkopf Beispiel
                </div>
                <div className="text-right">
                  <span
                    className="text-xl font-bold"
                    style={{ color: primaryColor }}
                  >
                    12.345,67 €
                  </span>
                </div>
              </div>
            </div>
          </div>

          <Separator />

          <div className="flex justify-end">
            <Button
              onClick={handleSaveColors}
              disabled={savingColors}
              data-testid="save-colors-btn"
            >
              {savingColors ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Save className="h-4 w-4 mr-2" />
              )}
              Farben speichern
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Info */}
      <Card className="border-blue-200 bg-blue-50 dark:bg-blue-950/30 dark:border-blue-800">
        <CardContent className="flex items-start gap-3 py-4">
          <Eye className="h-5 w-5 text-blue-600 mt-0.5" />
          <div>
            <p className="font-medium text-blue-800 dark:text-blue-200">
              PDF-Vorschau
            </p>
            <p className="text-sm text-blue-700 dark:text-blue-300">
              Die CI-Einstellungen werden in allen neu generierten Angebots-PDFs verwendet.
              Bereits erstellte PDFs werden nicht automatisch aktualisiert.
            </p>
          </div>
        </CardContent>
      </Card>

      {/* SMTP Settings Link */}
      <Card 
        className="cursor-pointer hover:bg-muted/50 transition-colors"
        onClick={() => navigate("/einstellungen/email")}
        data-testid="smtp-settings-card"
      >
        <CardContent className="flex items-center justify-between py-6">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-lg bg-primary/10 flex items-center justify-center">
              <Mail className="h-5 w-5 text-primary" />
            </div>
            <div>
              <p className="font-medium">SMTP-Einstellungen</p>
              <p className="text-sm text-muted-foreground">
                E-Mail-Server für Kaufverträge und Auftragsbestätigungen konfigurieren
              </p>
            </div>
          </div>
          <ChevronRight className="h-5 w-5 text-muted-foreground" />
        </CardContent>
      </Card>

      {/* AB-Textbausteine (Auftragsbestätigung) */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Settings2 className="h-5 w-5" />
            Textbausteine — Auftragsbestätigung
          </CardTitle>
          <CardDescription>
            Anrede, Haupttext und Schlussformel für die Auftragsbestätigungs-PDFs.
            <br />
            <span className="text-muted-foreground text-xs">
              Verfügbare Platzhalter: <code className="bg-muted px-1 py-0.5 rounded">{"{salutation}"}</code>, <code className="bg-muted px-1 py-0.5 rounded">{"{first_name}"}</code>, <code className="bg-muted px-1 py-0.5 rounded">{"{last_name}"}</code>, <code className="bg-muted px-1 py-0.5 rounded">{"{work_description}"}</code>, <code className="bg-muted px-1 py-0.5 rounded">{"{offer_number}"}</code>, <code className="bg-muted px-1 py-0.5 rounded">{"{project_number}"}</code>, <code className="bg-muted px-1 py-0.5 rounded">{"{company_name}"}</code> (nur Schlussformel)
            </span>
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label htmlFor="ab-greeting">Anrede</Label>
            <Textarea
              id="ab-greeting"
              rows={2}
              value={abTexts.order_confirmation_greeting}
              onChange={(e) => setAbTexts({ ...abTexts, order_confirmation_greeting: e.target.value })}
              placeholder="Sehr geehrte(r) {salutation} {last_name},"
              data-testid="ab-greeting-input"
            />
            <p className="text-xs text-muted-foreground mt-1">Standard: „Sehr geehrte(r) {"{salutation}"} {"{last_name}"},"</p>
          </div>
          <div>
            <Label htmlFor="ab-body">Haupttext</Label>
            <Textarea
              id="ab-body"
              rows={4}
              value={abTexts.order_confirmation_body}
              onChange={(e) => setAbTexts({ ...abTexts, order_confirmation_body: e.target.value })}
              placeholder="vielen Dank für Ihren Auftrag. Wir freuen uns, Ihnen hiermit die Durchführung der {work_description} gemäß Angebot Nr. {offer_number} zu bestätigen."
              data-testid="ab-body-input"
            />
          </div>
          <div>
            <Label htmlFor="ab-footer">Schlussformel</Label>
            <Textarea
              id="ab-footer"
              rows={4}
              value={abTexts.order_confirmation_footer}
              onChange={(e) => setAbTexts({ ...abTexts, order_confirmation_footer: e.target.value })}
              placeholder={"Bei Fragen stehen wir Ihnen gerne zur Verfügung.\nMit freundlichen Grüßen,\n{company_name}"}
              data-testid="ab-footer-input"
            />
            <p className="text-xs text-muted-foreground mt-1">Jede neue Zeile wird im PDF als eigener Absatz dargestellt.</p>
          </div>

          {/* Zahlungshinweis-Sektion */}
          <div className="border-t pt-4 space-y-3">
            <div className="flex items-center justify-between p-3 bg-muted/40 rounded-lg">
              <div>
                <Label htmlFor="ab-show-payment-terms" className="cursor-pointer font-medium">Zahlungshinweis im PDF anzeigen</Label>
                <p className="text-xs text-muted-foreground mt-0.5">Steuert, ob der Block &bdquo;Zahlungshinweis&rdquo; auf der Auftragsbestätigung erscheint.</p>
              </div>
              <Switch
                id="ab-show-payment-terms"
                checked={abTexts.order_confirmation_show_payment_terms}
                onCheckedChange={(checked) => setAbTexts({ ...abTexts, order_confirmation_show_payment_terms: checked })}
                data-testid="ab-show-payment-terms-switch"
              />
            </div>
            {abTexts.order_confirmation_show_payment_terms && (
              <div>
                <Label htmlFor="ab-payment-terms">Eigener Zahlungshinweis-Text (optional)</Label>
                <Textarea
                  id="ab-payment-terms"
                  rows={3}
                  value={abTexts.order_confirmation_payment_terms}
                  onChange={(e) => setAbTexts({ ...abTexts, order_confirmation_payment_terms: e.target.value })}
                  placeholder="Leer lassen für den Standard-Text (60% in 7 Tagen bei Ratenzahlung, 100% vor Installation bei Vorkasse)."
                  data-testid="ab-payment-terms-input"
                />
                <p className="text-xs text-muted-foreground mt-1">Wenn ausgefüllt, ersetzt dieser Text den vorgegebenen Zahlungshinweis – unabhängig von der Zahlungsart.</p>
              </div>
            )}
          </div>

          <Button onClick={handleSaveAbTexts} disabled={savingAbTexts} data-testid="save-ab-texts-btn">
            {savingAbTexts ? "Speichern…" : "AB-Textbausteine speichern"}
          </Button>
        </CardContent>
      </Card>

      {/* VAT Settings */}
      <Card>        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Settings2 className="h-5 w-5" />
            MwSt-Einstellungen
          </CardTitle>
          <CardDescription>
            Festlegen, welche Produktkategorien mit Mehrwertsteuer berechnet werden.
            <br />
            <span className="text-amber-600 font-medium">
              Änderungen wirken sich nur auf neue Dokumente aus.
            </span>
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="space-y-4">
            <div className="flex flex-col gap-2 p-3 bg-muted rounded-lg">
              <div className="flex items-center justify-between gap-3">
                <p className="font-medium">Wallbox / Ladestation</p>
                <label className="relative inline-flex items-center cursor-pointer shrink-0">
                  <input
                    type="checkbox"
                    checked={vatSettings.vat_enabled_wallbox}
                    onChange={(e) => setVatSettings({...vatSettings, vat_enabled_wallbox: e.target.checked})}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary/20 rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary"></div>
                  <span className="ms-3 text-sm font-medium text-gray-900 dark:text-gray-300">
                    {vatSettings.vat_enabled_wallbox ? 'Mit MwSt' : 'Ohne MwSt'}
                  </span>
                </label>
              </div>
              <Textarea
                value={vatSettings.vat_label_wallbox || ""}
                onChange={(e) => setVatSettings({...vatSettings, vat_label_wallbox: e.target.value})}
                placeholder="Beschreibung / Rechtsgrundlage..."
                className="text-sm bg-white"
                rows={2}
                maxLength={500}
                data-testid="vat-label-wallbox"
              />
            </div>
            
            <div className="flex flex-col gap-2 p-3 bg-muted rounded-lg">
              <div className="flex items-center justify-between gap-3">
                <p className="font-medium">PV-Anlage (Module & Wechselrichter)</p>
                <label className="relative inline-flex items-center cursor-pointer shrink-0">
                  <input
                    type="checkbox"
                    checked={vatSettings.vat_enabled_pv}
                    onChange={(e) => setVatSettings({...vatSettings, vat_enabled_pv: e.target.checked})}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary/20 rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary"></div>
                  <span className="ms-3 text-sm font-medium text-gray-900 dark:text-gray-300">
                    {vatSettings.vat_enabled_pv ? 'Mit MwSt' : 'Ohne MwSt'}
                  </span>
                </label>
              </div>
              <Textarea
                value={vatSettings.vat_label_pv || ""}
                onChange={(e) => setVatSettings({...vatSettings, vat_label_pv: e.target.value})}
                placeholder="Beschreibung / Rechtsgrundlage..."
                className="text-sm bg-white"
                rows={2}
                maxLength={500}
                data-testid="vat-label-pv"
              />
            </div>
            
            <div className="flex flex-col gap-2 p-3 bg-muted rounded-lg">
              <div className="flex items-center justify-between gap-3">
                <p className="font-medium">Stromspeicher</p>
                <label className="relative inline-flex items-center cursor-pointer shrink-0">
                  <input
                    type="checkbox"
                    checked={vatSettings.vat_enabled_storage}
                    onChange={(e) => setVatSettings({...vatSettings, vat_enabled_storage: e.target.checked})}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary/20 rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary"></div>
                  <span className="ms-3 text-sm font-medium text-gray-900 dark:text-gray-300">
                    {vatSettings.vat_enabled_storage ? 'Mit MwSt' : 'Ohne MwSt'}
                  </span>
                </label>
              </div>
              <Textarea
                value={vatSettings.vat_label_storage || ""}
                onChange={(e) => setVatSettings({...vatSettings, vat_label_storage: e.target.value})}
                placeholder="Beschreibung / Rechtsgrundlage..."
                className="text-sm bg-white"
                rows={2}
                maxLength={500}
                data-testid="vat-label-storage"
              />
            </div>
            
            <div className="flex flex-col gap-2 p-3 bg-muted rounded-lg">
              <div className="flex items-center justify-between gap-3">
                <p className="font-medium">Sonstiges</p>
                <label className="relative inline-flex items-center cursor-pointer shrink-0">
                  <input
                    type="checkbox"
                    checked={vatSettings.vat_enabled_other}
                    onChange={(e) => setVatSettings({...vatSettings, vat_enabled_other: e.target.checked})}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary/20 rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary"></div>
                  <span className="ms-3 text-sm font-medium text-gray-900 dark:text-gray-300">
                    {vatSettings.vat_enabled_other ? 'Mit MwSt' : 'Ohne MwSt'}
                  </span>
                </label>
              </div>
              <Textarea
                value={vatSettings.vat_label_other || ""}
                onChange={(e) => setVatSettings({...vatSettings, vat_label_other: e.target.value})}
                placeholder="Beschreibung / Rechtsgrundlage..."
                className="text-sm bg-white"
                rows={2}
                maxLength={500}
                data-testid="vat-label-other"
              />
            </div>
          </div>
          
          <Separator />
          
          <div className="space-y-2">
            <Label>MwSt-Satz (%)</Label>
            <Input
              type="number"
              min="0"
              max="100"
              step="0.1"
              value={vatSettings.default_vat_rate}
              onChange={(e) => setVatSettings({...vatSettings, default_vat_rate: parseFloat(e.target.value) || 19})}
              className="w-32"
            />
            <p className="text-xs text-muted-foreground">
              Standard: 19% (wird auf Produkte mit aktivierter MwSt angewendet)
            </p>
          </div>

          <div className="space-y-2 pt-2 border-t">
            <Label>Lohnkostenanteil §35a EStG (%)</Label>
            <Input
              type="number"
              min="0"
              max="100"
              step="0.5"
              value={vatSettings.default_labor_cost_percentage ?? 30}
              onChange={(e) => setVatSettings({...vatSettings, default_labor_cost_percentage: parseFloat(e.target.value) || 0})}
              className="w-32"
              data-testid="labor-cost-percentage-input"
            />
            <p className="text-xs text-muted-foreground">
              Standard: 30%. Dieser Prozentsatz wird auf Schlussrechnungen mit Nullbetrag als
              steuerlich absetzbarer Lohnkostenanteil (§35a EStG) ausgewiesen.
            </p>
          </div>
          
          <div className="flex justify-end">
            <Button
              onClick={handleSaveVatSettings}
              disabled={savingVat}
              data-testid="save-vat-btn"
            >
              {savingVat ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Save className="h-4 w-4 mr-2" />
              )}
              MwSt-Einstellungen speichern
            </Button>
          </div>
        </CardContent>
      </Card>
        </>
      )}
    </div>
  );
}

// Separate component for Tax Info to keep state isolated
function TaxInfoCard({ company, onUpdate }) {
  const [taxId, setTaxId] = useState(company?.tax_id || "");
  const [taxNumber, setTaxNumber] = useState(company?.tax_number || "");
  const [saving, setSaving] = useState(false);
  
  // Update local state when company changes
  React.useEffect(() => {
    setTaxId(company?.tax_id || "");
    setTaxNumber(company?.tax_number || "");
  }, [company]);
  
  const handleSave = async () => {
    if (!taxId && !taxNumber) {
      toast.error("Bitte geben Sie mindestens USt-ID oder Steuernummer ein");
      return;
    }
    
    setSaving(true);
    try {
      await companiesAPI.update(company.id, {
        ...company,
        tax_id: taxId || null,
        tax_number: taxNumber || null
      });
      toast.success("Steuerdaten gespeichert");
      onUpdate();
    } catch (error) {
      console.error("Failed to save tax info:", error);
      toast.error("Fehler beim Speichern der Steuerdaten");
    } finally {
      setSaving(false);
    }
  };
  
  const hasTaxInfo = taxId || taxNumber;
  
  return (
    <Card className={!hasTaxInfo ? "border-amber-300 bg-amber-50 dark:bg-amber-950/20" : ""}>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Building2 className="h-5 w-5" />
          Steuerdaten
          {!hasTaxInfo && (
            <span className="text-sm font-normal text-amber-600 bg-amber-100 px-2 py-0.5 rounded">
              Erforderlich für Rechnungen
            </span>
          )}
        </CardTitle>
        <CardDescription>
          Mindestens USt-ID oder Steuernummer wird für die Rechnungsfinalisierung benötigt.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="tax_id">USt-ID (Umsatzsteuer-ID)</Label>
            <Input
              id="tax_id"
              value={taxId}
              onChange={(e) => setTaxId(e.target.value)}
              placeholder="DE123456789"
              data-testid="tax-id-input"
            />
            <p className="text-xs text-muted-foreground">
              Für EU-Geschäfte erforderlich
            </p>
          </div>
          <div className="space-y-2">
            <Label htmlFor="tax_number">Steuernummer</Label>
            <Input
              id="tax_number"
              value={taxNumber}
              onChange={(e) => setTaxNumber(e.target.value)}
              placeholder="123/456/78901"
              data-testid="tax-number-input"
            />
            <p className="text-xs text-muted-foreground">
              Steuernummer vom Finanzamt
            </p>
          </div>
        </div>
        
        {!hasTaxInfo && (
          <div className="p-3 bg-amber-100 dark:bg-amber-900/30 rounded-lg text-amber-800 dark:text-amber-200 text-sm">
            <strong>Wichtig:</strong> Ohne USt-ID oder Steuernummer können Rechnungen nicht finalisiert werden.
          </div>
        )}
        
        <div className="flex justify-end">
          <Button onClick={handleSave} disabled={saving} data-testid="save-tax-btn">
            {saving ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <Save className="h-4 w-4 mr-2" />
            )}
            Steuerdaten speichern
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

function BankInfoCard({ company, onUpdate }) {
  const [bankName, setBankName] = useState(company?.bank_name || "");
  const [bankIban, setBankIban] = useState(company?.bank_iban || "");
  const [bankBic, setBankBic] = useState(company?.bank_bic || "");
  const [saving, setSaving] = useState(false);
  
  // Update local state when company changes
  React.useEffect(() => {
    setBankName(company?.bank_name || "");
    setBankIban(company?.bank_iban || "");
    setBankBic(company?.bank_bic || "");
  }, [company]);
  
  const handleSave = async () => {
    if (!bankIban) {
      toast.error("Bitte geben Sie mindestens die IBAN ein");
      return;
    }
    
    setSaving(true);
    try {
      await companiesAPI.update(company.id, {
        ...company,
        bank_name: bankName || null,
        bank_iban: bankIban || null,
        bank_bic: bankBic || null
      });
      toast.success("Bankverbindung gespeichert");
      onUpdate();
    } catch (error) {
      console.error("Failed to save bank info:", error);
      toast.error("Fehler beim Speichern der Bankverbindung");
    } finally {
      setSaving(false);
    }
  };
  
  const hasBankInfo = bankIban;
  
  return (
    <Card className={!hasBankInfo ? "border-amber-300 bg-amber-50 dark:bg-amber-950/20" : ""}>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Building2 className="h-5 w-5" />
          Bankverbindung
          {!hasBankInfo && (
            <span className="text-sm font-normal text-amber-600 bg-amber-100 px-2 py-0.5 rounded">
              Erforderlich für Rechnungen
            </span>
          )}
        </CardTitle>
        <CardDescription>
          Bankverbindung für Zahlungsinformationen auf Rechnungen.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="space-y-2">
            <Label htmlFor="bank_name">Bank</Label>
            <Input
              id="bank_name"
              value={bankName}
              onChange={(e) => setBankName(e.target.value)}
              placeholder="Volksbank Musterstadt"
              data-testid="bank-name-input"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="bank_iban">IBAN</Label>
            <Input
              id="bank_iban"
              value={bankIban}
              onChange={(e) => setBankIban(e.target.value)}
              placeholder="DE89 3704 0044 0532 0130 00"
              data-testid="bank-iban-input"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="bank_bic">BIC</Label>
            <Input
              id="bank_bic"
              value={bankBic}
              onChange={(e) => setBankBic(e.target.value)}
              placeholder="COBADEFFXXX"
              data-testid="bank-bic-input"
            />
          </div>
        </div>
        
        {!hasBankInfo && (
          <div className="p-3 bg-amber-100 dark:bg-amber-900/30 rounded-lg text-amber-800 dark:text-amber-200 text-sm">
            <strong>Wichtig:</strong> Ohne Bankverbindung fehlen die Zahlungsinformationen auf Ihren Rechnungen.
          </div>
        )}
        
        <div className="flex justify-end">
          <Button onClick={handleSave} disabled={saving} data-testid="save-bank-btn">
            {saving ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <Save className="h-4 w-4 mr-2" />
            )}
            Bankverbindung speichern
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

// Separate component for Company Info (Address, Phone, Email) to keep state isolated
function CompanyInfoCard({ company, onUpdate }) {
  const [address, setAddress] = useState(company?.address || "");
  const [phone, setPhone] = useState(company?.phone || "");
  const [email, setEmail] = useState(company?.email || "");
  const [saving, setSaving] = useState(false);
  
  // Update local state when company changes
  React.useEffect(() => {
    setAddress(company?.address || "");
    setPhone(company?.phone || "");
    setEmail(company?.email || "");
  }, [company]);
  
  const handleSave = async () => {
    if (!address) {
      toast.error("Bitte geben Sie die Firmenadresse ein");
      return;
    }
    
    setSaving(true);
    try {
      await companiesAPI.update(company.id, {
        ...company,
        address: address || null,
        phone: phone || null,
        email: email || null
      });
      toast.success("Firmendaten gespeichert");
      onUpdate();
    } catch (error) {
      console.error("Failed to save company info:", error);
      toast.error("Fehler beim Speichern der Firmendaten");
    } finally {
      setSaving(false);
    }
  };
  
  const hasAddress = !!address;
  
  return (
    <Card className={!hasAddress ? "border-amber-300 bg-amber-50 dark:bg-amber-950/20" : ""}>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Building2 className="h-5 w-5" />
          Firmenadresse & Kontakt
          {!hasAddress && (
            <span className="text-sm font-normal text-amber-600 bg-amber-100 px-2 py-0.5 rounded">
              Erforderlich für Rechnungen
            </span>
          )}
        </CardTitle>
        <CardDescription>
          Diese Daten erscheinen auf Angeboten und Rechnungen.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="space-y-2">
          <Label htmlFor="company_address">Firmenadresse *</Label>
          <Input
            id="company_address"
            value={address}
            onChange={(e) => setAddress(e.target.value)}
            placeholder="Musterstraße 123, 12345 Berlin"
            data-testid="company-address-input"
          />
          <p className="text-xs text-muted-foreground">
            Vollständige Adresse inkl. PLZ und Ort
          </p>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="company_phone">Telefon</Label>
            <Input
              id="company_phone"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              placeholder="+49 123 456789"
              data-testid="company-phone-input"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="company_email">E-Mail</Label>
            <Input
              id="company_email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="info@firma.de"
              data-testid="company-email-input"
            />
          </div>
        </div>
        
        {!hasAddress && (
          <div className="p-3 bg-amber-100 dark:bg-amber-900/30 rounded-lg text-amber-800 dark:text-amber-200 text-sm">
            <strong>Wichtig:</strong> Ohne Firmenadresse können Rechnungen nicht ordnungsgemäß erstellt werden.
          </div>
        )}
        
        <div className="flex justify-end">
          <Button onClick={handleSave} disabled={saving} data-testid="save-company-info-btn">
            {saving ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <Save className="h-4 w-4 mr-2" />
            )}
            Firmendaten speichern
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
